import React, { useContext, useEffect, useState, useMemo } from "react";
import { Header } from "components/header";
import "material-symbols/outlined.css";
import StoreFormContainer from "components/store-form-container";
import TradeInDetails from "components/trade-in-details";
import AccountModal from "components/account-modal";
import { getQueryParams, isDropoffStore } from "lib/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { calculateEstimate } from "lib/calculateEstimate";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useTakebackById } from "../../api/use-takebacks";
import { useAddStatus } from "../../api/tes/useAddStatus";
import { useGenerateShipping } from "api/use-shipping";
import stores from "consts/stores.json";
import AuthContext from "contexts/AuthContext";
import { getAppointmentBySysId } from "components/appointments/service";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat.js";
import utc from "dayjs/plugin/utc.js";
import timezone from "dayjs/plugin/timezone.js";

dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export default function TradeInQr() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currencySymbol = t("currency.symbol");
  const location = useLocation();
  const queryParams = getQueryParams(location.search);
  const id = queryParams.id;
  const { user, authTokens } = useContext(AuthContext);
  const destination = isDropoffStore(user)
    ? `/en-US/store/trade-in-weight/dropoff`
    : t("urls.store_trade_in_weight");
  const takeback = useTakebackById({ id });
  const { mutate: addStatus } = useAddStatus();
  const [isGeneratingLabel, setIsGeneratingLabel] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [showEditSellerModal, setShowEditSellerModal] = useState(false);
  const [currentLocation, setCurrentLocation] = useState({ state: "" });

  // Extract shipping label data from GraphQL structure
  const shippingLabel =
    takeback?.data?.u_ups_shipping_label_encoded ||
    takeback?.data?.u_lego_dhl_label_b64_encoded;
  const originalLabelUrl =
    takeback?.data?.u_ups_shipping_label_url ||
    takeback?.shipping_metadatas?.items?.find(
      (item) => item.key === "label_url"
    )?.value;

  // Set up shipping generation hook
  const generateShipping = useGenerateShipping({
    onSuccess: (data) => {
      setIsGeneratingLabel(false);
      // Extract the label URL from the response and open it immediately
      const shippingData = data?.data?.[0];
      const originalLabelUrl = shippingData?.shipping_metadatas?.items?.find(
        (item) => item.key === "label_url"
      )?.value;

      if (originalLabelUrl && queryParams.id) {
        try {
          const url = new URL(originalLabelUrl);
          const returnID = url.searchParams.get("returnID");

          if (returnID && authTokens?.access_token) {
            const labelUrl = `${window.location.origin}/api/pdf/shipping-label-no-instructions?returnID=${returnID}&token=${authTokens.access_token}`;
            // Open PDF in new tab for printing
            window.open(labelUrl, "_blank");
          } else {
            window.open(originalLabelUrl, "_blank");
          }
        } catch (error) {
          console.error("Error constructing label URL:", error);
          window.open(originalLabelUrl, "_blank");
        }
      }
    },
    onError: () => {
      setIsGeneratingLabel(false);
      window.alert("Failed to generate shipping label");
    },
  });

  // Construct label URL
  const labelUrl = useMemo(() => {
    if (originalLabelUrl && queryParams.id) {
      try {
        const url = new URL(originalLabelUrl);
        const returnID = url.searchParams.get("returnID");

        if (returnID && authTokens?.access_token) {
          return `${window.location.origin}/api/pdf/shipping-label-no-instructions?returnID=${returnID}&token=${authTokens.access_token}`;
        }
        return originalLabelUrl;
      } catch (error) {
        console.error("Error constructing label URL:", error);
        return originalLabelUrl;
      }
    }
    return originalLabelUrl;
  }, [originalLabelUrl, queryParams.id, authTokens.access_token]);

  const handleSubmit = async () => {
    const verifiedAt =
      takeback?.rawTakeback.takeback_processing_history_list.find(
        ({ parent_status }) => parent_status === "processed"
      )?.updated_at;

    if (verifiedAt) {
      // Trade-in is verified - handle printing
      const shippingPresent = !!(shippingLabel || originalLabelUrl);

      if (shippingPresent && labelUrl) {
        // Open PDF in new tab for printing
        window.open(labelUrl, "_blank");
      } else {
        // No shipping label available - generate one
        if (isGeneratingLabel) return; // Prevent double-clicks

        // Extract customer ID and takeback ID for shipping generation
        const customerId = takeback?.consumer.value;
        const takebackId = takeback?.number;

        if (!customerId || !takebackId) {
          window.alert(
            "Missing seller or takeback information required for shipping label generation"
          );
          return;
        }

        setIsGeneratingLabel(true);
        generateShipping.mutate({
          takeback_id: takebackId,
          customer_id: customerId,
          type: "MAIL_LABEL", // Generate printable shipping label
        });
      }
    } else {
      // Trade-in not verified yet - update status and navigate to weight screen
      const receivedAt = new Date().toISOString();
      localStorage.setItem("receivedAt", receivedAt);

      // Update takeback status for checkin and await completion
      await addStatus({
        id: takeback.sys_id,
        input: {
          status: "in-store-checkin",
          parent_status: "processing",
          updated_at: parseInt(Date.now() / 1000),
        },
      });

      // Navigate after status update is complete
      navigate({
        pathname: destination,
        search: new URLSearchParams(queryParams).toString(),
      });
    }
  };

  const handleCancel = () => {
    navigate({
      pathname: t(`urls.store_check_in`),
      search: new URLSearchParams(queryParams).toString(),
    });
  };

  useEffect(() => {
    if (user.state) {
      setCurrentLocation({ state: user.state });
    }

    if (takeback) {
      getAppointmentBySysId(takeback.sys_id).then((res) => {
        setAppointment(res);
      });
    }
  }, [takeback, user.state]);

  if (!takeback) {
    return null;
  }

  const verifiedAt = takeback.rawTakeback.takeback_processing_history_list.find(
    ({ parent_status }) => parent_status === "processed"
  )?.updated_at;
  const verifiedWeight = takeback.rawTakeback?.data?.conform_weight;
  const parsedVerifiedWeight = parseFloat(verifiedWeight).toFixed(2);
  const verifiedValue = calculateEstimate(
    takeback?.rawTakeback.data.conform_weight || 0,
    takeback?.u_lego_incentive_ratio
  ).gift;
  const payoutMethod = takeback?.u_lego_payment_type;
  const takebackMethod = takeback?.u_lego_return_type;

  const weight = takeback?.u_lego_presort_weight;
  const parsedWeight = parseFloat(weight).toFixed(2);
  const estimate = calculateEstimate(weight);
  const estimatedValue =
    payoutMethod === "giftcard" ? estimate.gift : estimate.payment;

  return (
    <StoreFormContainer
      submitLabel={
        verifiedAt
          ? isGeneratingLabel
            ? "Generating Label..."
            : "Print Shipping Label"
          : "Start Trade-In"
      }
      secondaryLabel={verifiedAt ? "Close" : "Cancel"}
      onSubmit={handleSubmit}
      onSecondary={handleCancel}
      disabled={isGeneratingLabel}
      stickyFooter={true}
    >
      <Header />
      <div className="flex flex-grow flex-col py-4 mt-[81px]">
        <h2 className="font-semibold text-6xl max-w-lg pt-2">Trade-In</h2>
        <div className="flex flex-col">
          <div className="text-2xl text-black-300 mb-4">
            {verifiedAt ? "Payment Issued" : "Trade-in Created"}
          </div>

          <div className="pt-8">
            <TradeInDetails
              customerName={`${takeback.consumer.firstName.trim()} ${takeback.consumer.lastName.trim()}`}
              address={`${takeback.consumer.street.trim()}, ${takeback.consumer.city.trim()}, ${takeback.consumer.zip.trim()}`}
              email={takeback.consumer.email.trim()}
              tradeInId={takeback.sys_id}
              dateCreated={format(
                takeback.sys_created_on
                  ? new Date(takeback.sys_created_on)
                  : new Date(),
                `MMMM d, yyyy 'at' h:mm a`
              )}
              dateVerified={
                verifiedAt
                  ? format(new Date(verifiedAt), `MMMM d, yyyy 'at' h:mm a`)
                  : null
              }
              verifiedQuantity={
                verifiedAt && verifiedWeight
                  ? `${parseFloat(parsedVerifiedWeight).toFixed(1)}`
                  : `${parseFloat(parsedWeight).toFixed(1)}`
              }
              originalEstimate={
                takeback.rawTakeback.data?.originalEstimatedWeight
              }
              paymentDetails={
                verifiedAt &&
                !isNaN(estimate.payment) &&
                verifiedValue !== "0.00"
                  ? `${currencySymbol}${verifiedValue} LEGO® e-Gift Card`
                  : !isNaN(estimate.payment) && estimatedValue !== "0.00"
                    ? `${currencySymbol}${estimatedValue} LEGO® e-Gift Card`
                    : null
              }
              location={
                stores.find((store) => store.value === takebackMethod)
                  ?.address || "Mail In"
              }
              appointment={appointment}
              currentLocation={currentLocation}
              takebackIsVerified={!!verifiedAt}
              isDropoffStore={
                !stores.some(
                  (store) =>
                    takeback.u_lego_return_type.includes(store.value) &&
                    store.return_type === "kiosk"
                )
              }
              showEditSeller={true}
              onEditSeller={() => setShowEditSellerModal(true)}
              showManagePayment={false}
            />
          </div>
        </div>
      </div>

      {/* Edit Seller Modal */}
      <AccountModal
        onClose={() => setShowEditSellerModal(false)}
        show={showEditSellerModal}
        customerId={takeback?.consumer?.value}
      />
    </StoreFormContainer>
  );
}
